const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1100;
const QUALITY = 0.8;

const extensionOf = (name: string) => name.slice(name.lastIndexOf(".")).toLowerCase();

const COMPRESSIBLE = [".png", ".jpg", ".jpeg"];

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    image.src = url;
  });

/** Scaled down to fit 1200x1100 at quality 80; a PNG stays a PNG, anything else becomes a JPEG. */
const compressImage = async (file: File): Promise<File> => {
  const image = await loadImage(file);
  const scale = Math.min(1, MAX_WIDTH / image.width, MAX_HEIGHT / image.height);
  const canvas = document.createElement("canvas");

  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);

  const isPng = extensionOf(file.name) === ".png";
  const type = isPng ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, QUALITY));

  if (!blob) {
    return file;
  }

  const name = isPng ? file.name : file.name.replace(/\.[^.]+$/, ".jpg");

  return new File([blob], name, { type });
};

/**
 * What actually goes up with a booking, as on mobile: PDFs untouched, `.png/.jpg/.jpeg` compressed,
 * and any other image format (.heic, .webp...) left out - the server only takes those three.
 */
export const prepareAttachmentsForUpload = async (files: File[]) => {
  const prepared = await Promise.all(
    files.map(async (file) => {
      const extension = extensionOf(file.name);

      if (extension === ".pdf") {
        return file;
      }

      if (!COMPRESSIBLE.includes(extension)) {
        return undefined;
      }

      try {
        return await compressImage(file);
      } catch {
        // A browser that cannot decode it still gets the original rather than nothing.
        return file;
      }
    }),
  );

  return prepared.filter((file): file is File => !!file);
};
